import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { AppLayout } from '@/app/layout/AppLayout';
import { ManageLayout } from '@/app/layout/ManageLayout';
import { ManageIndexPage } from '@/app/pages/ManageIndexPage';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { resolveStartRoute } from '@/app/navigation';
import { useEdition } from '@/editions';
import { CallbackPage } from '@/features/auth/CallbackPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { ChallengesPage } from '@/features/challenges/ChallengesPage';
import { CommunicationsPage } from '@/features/communications/CommunicationsPage';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { ROUTE_MODULES } from '@/features/company/modules';
import { DepartmentComparePage } from '@/features/departments/DepartmentComparePage';
import { DepartmentDetailPage } from '@/features/departments/DepartmentDetailPage';
import {
  DepartmentCollectiveConsentPage,
  DepartmentEditPage,
  DepartmentNewPage,
  DepartmentVisibilityPage,
} from '@/features/departments/DepartmentFormPages';
import { DepartmentsPage } from '@/features/departments/DepartmentsPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { MeProvider, useMe } from '@/features/me/MeContext';
import { ConsentsPage } from '@/features/profile/ConsentsPage';
import { PreferencesPage } from '@/features/profile/PreferencesPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ProfileShell } from '@/features/profile/ProfileShell';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { SettingsBrandingPage } from '@/features/settings/SettingsBrandingPage';
import { SettingsModulesPage } from '@/features/settings/SettingsModulesPage';
import { SettingsPrivacyPage } from '@/features/settings/SettingsPrivacyPage';
import { SettingsProfilePage } from '@/features/settings/SettingsProfilePage';
import { SettingsShell } from '@/features/settings/SettingsShell';
import { CheckoutPage } from '@/features/subscription/CheckoutPage';
import { ReturnPage as SubscriptionReturnPage } from '@/features/subscription/ReturnPage';
import { SubscriptionPage } from '@/features/subscription/SubscriptionPage';
import { WithdrawPage } from '@/features/subscription/WithdrawPage';
import { OverviewPage } from '@/features/overview/OverviewPage';
import { PilotDetailPage } from '@/features/pilots/PilotDetailPage';
import { PilotsPage } from '@/features/pilots/PilotsPage';
import { CompanyAddPersonPage, DepartmentAddPersonPage } from '@/features/users/AddPersonPage';
import { UserDetailPage } from '@/features/users/UserDetailPage';
import { UserEditPage } from '@/features/users/UserEditPage';
import { UsersPage } from '@/features/users/UsersPage';
import { VitalityPage } from '@/features/vitality/VitalityPage';
import { ParticipationPage } from '@/features/participation/ParticipationPage';
import { LEGACY_MANAGE_REDIRECTS } from '@/manage/navigation';
import { AuditPage } from '@/manage/audit/AuditPage';
import { CompaniesPage } from '@/manage/companies/CompaniesPage';
import { CompanyDetailPage } from '@/manage/companies/CompanyDetailPage';
import {
  CompanyDeletePage,
  CompanyDomainsPage,
  CompanyEditPage,
  CompanyNewPage,
  CompanySettingsPage,
} from '@/manage/companies/CompanyFormPages';
import {
  CompanyRolesPage,
  DepartmentRolesPage,
  PlatformAdminsPage,
} from '@/manage/companies/RolePages';
import { ErasePage } from '@/manage/people/ErasePage';
import { LinkIdentitiesPage } from '@/manage/people/LinkIdentitiesPage';
import { UsersPage as ManageUsersPage } from '@/manage/people/UsersPage';
import { PlaceholderPage } from '@/manage/PlaceholderPage';
import { AdminOverviewPage } from '@/manage/overview/AdminOverviewPage';
import { PilotsOverviewPage } from '@/manage/pilots/PilotsOverviewPage';
import { ConfigPage as SubscriptionsConfigPage } from '@/manage/subscriptions/ConfigPage';
import { ProductsPage as SubscriptionsProductsPage } from '@/manage/subscriptions/ProductsPage';
import { UserLedgerPage as SubscriptionsUserLedgerPage } from '@/manage/subscriptions/UserLedgerPage';
import { SupportTicketDetailPage } from '@/manage/support/SupportTicketDetailPage';
import { SupportTicketsPage } from '@/manage/support/SupportTicketsPage';
import { RequestsPage } from '@/manage/system/RequestsPage';

/**
 * Renders `children` only when the current path belongs to the caller's
 * edition (one of its sections' `match` prefixes) — a shared link into a
 * page the edition doesn't offer renders `NotFoundPage` instead of a page
 * that shouldn't exist for it. Edition membership is independent of
 * permission: `match` says whether the *edition* has the page at all,
 * `visible` (used by the menu, not here) says whether *this caller* may see
 * it — a 403 from the API remains the real boundary either way.
 */
export function EditionRoute({ children }: { children: ReactElement }): ReactElement {
  const edition = useEdition();
  const location = useLocation();
  const inEdition = edition.sections.some((section) =>
    section.match.some((prefix) => location.pathname.startsWith(prefix)),
  );
  return inEdition ? children : <NotFoundPage />;
}

/**
 * Guards the 7 module-gated routes (PLAN-FASE3.md WP-06, closing WP-00's
 * "Enforcement van Modules-toggles" open point): `ROUTE_MODULES` maps a path
 * to its module key, and a direct URL visit to a route whose module the
 * active company has switched off redirects to `/overview`, same "hide the
 * nav, and also block the direct URL" pattern as `EditionRoute` — a shared
 * link into a disabled module shouldn't reach the page just because the nav
 * item is gone. No active company (personal context) or the modules fetch
 * not back yet both read as enabled (`isModuleEnabled`'s default), so this
 * never blocks before the real answer is in. `ai_coach` has no page and
 * isn't in `ROUTE_MODULES`, so it's never checked here.
 */
export function ModuleRoute({ children }: { children: ReactElement }): ReactElement {
  const { isModuleEnabled } = useActiveCompany();
  const location = useLocation();
  const moduleKey = ROUTE_MODULES[location.pathname];
  if (moduleKey && !isModuleEnabled(moduleKey)) {
    return <Navigate to="/overview" replace />;
  }
  return children;
}

/**
 * Guards `/manage/*` (§ Beheer › Routes): `isAdmin || administeredCompanyIds.length > 0`
 * — a department admin/coach alone has no Beheer button and no `/manage`
 * access; their authority stays acts-on-the-department-page, in the normal
 * app.
 */
export function RequireManage({ children }: { children: ReactElement }): ReactElement {
  const { permissions } = useMe();
  return permissions.canManage ? children : <NotFoundPage />;
}

/** `/` (§ Zichtbaarheid, row "/"): sends the caller straight to the first
 * thing they may see — there is no Overview page (see `resolveStartRoute`). */
function StartRedirect() {
  const { me, permissions } = useMe();
  return <Navigate to={resolveStartRoute(me, permissions)} replace />;
}

/**
 * `/companies/*`, `/admins`, `/audit` (§ Beheer › Routes): the old top-level
 * paths, all relocated under `/manage` in WP-13.
 * `LEGACY_MANAGE_REDIRECTS` names the three list/landing targets
 * (`/manage/companies`, `/manage/admins`, `/manage/audit`); the trailing part
 * of the path, if any, rides along unchanged (`/companies/123/edit` →
 * `/manage/companies/123/edit`), so a bookmarked deep link still lands on the
 * right page. `/admins` and `/audit` have no trailing part to carry.
 */
function LegacyManageRedirect({ from }: { from: string }) {
  const params = useParams();
  const to = LEGACY_MANAGE_REDIRECTS.get(from);
  const rest = params['*'] ? `/${params['*']}` : '';
  return <Navigate to={`${to ?? '/manage'}${rest}`} replace />;
}

/**
 * The router. The callback route sits outside the shell — it renders while the
 * code exchange is still in flight, before there is a user to build a shell
 * for. Everything else lives behind RequireAuth and the `/me` payload: this
 * dashboard has no anonymous pages and nothing renders without knowing the
 * caller's scopes. Write pages are reachable by URL regardless of role — the
 * API is the boundary and a 403 renders as a message.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<CallbackPage />} />
      {/* Dev-only, outside auth: WP-05's Verify step. Removed in WP-16. */}

      {/* Old top-level paths, relocated under `/manage` (§ Beheer › Routes,
          WP-13) — `LEGACY_MANAGE_REDIRECTS` in `src/manage/navigation.ts`. */}
      <Route path="/companies/*" element={<LegacyManageRedirect from="/companies" />} />
      <Route path="/admins" element={<LegacyManageRedirect from="/admins" />} />
      <Route path="/audit" element={<LegacyManageRedirect from="/audit" />} />

      <Route
        element={
          <RequireAuth>
            <MeProvider>
              <AppLayout />
            </MeProvider>
          </RequireAuth>
        }
      >
        <Route index element={<StartRedirect />} />

        {/* The old standalone consents route now lives on /profile/consents. */}
        <Route path="consents" element={<Navigate to="/profile/consents" replace />} />

        {/* Companies moved under `/manage/companies` (§ Beheer, WP-13) — only
            the company name and the company-choice stay in the normal app. */}

        <Route
          path="departments"
          element={
            <EditionRoute>
              <DepartmentsPage />
            </EditionRoute>
          }
        />
        <Route
          path="departments/compare"
          element={
            <EditionRoute>
              <DepartmentComparePage />
            </EditionRoute>
          }
        />
        <Route path="departments/:departmentId" element={<DepartmentDetailPage />} />
        <Route path="departments/:departmentId/edit" element={<DepartmentEditPage />} />
        <Route path="departments/:departmentId/visibility" element={<DepartmentVisibilityPage />} />
        <Route
          path="departments/:departmentId/collective-consent"
          element={<DepartmentCollectiveConsentPage />}
        />
        <Route path="departments/:departmentId/roles" element={<DepartmentRolesPage />} />
        <Route path="departments/:departmentId/members/new" element={<DepartmentAddPersonPage />} />

        <Route
          path="users"
          element={
            <EditionRoute>
              <UsersPage />
            </EditionRoute>
          }
        />
        <Route path="users/:userId" element={<UserDetailPage />} />
        <Route path="users/:userId/edit" element={<UserEditPage />} />

        {/* Home pill, Overview subitem (§ Navigatiemodel, PLAN-FASE2.md
            WP-06) — company-dashboard summary, first subitem of Home. */}
        <Route
          path="overview"
          element={
            <EditionRoute>
              <OverviewPage />
            </EditionRoute>
          }
        />
        {/* Insights pill, Vitality subitem (§ Navigatiemodel, PLAN-FASE2.md
            WP-02) — Participation, the pill's other subitem, is WP-03. */}
        <Route
          path="vitality"
          element={
            <EditionRoute>
              <ModuleRoute>
                <VitalityPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* Insights pill, Participation subitem (§ Navigatiemodel,
            PLAN-FASE2.md WP-03) — the engagement funnel. */}
        <Route
          path="participation"
          element={
            <EditionRoute>
              <ModuleRoute>
                <ParticipationPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* WP-04 (§ Programs): pilots, scoped to the active company. */}
        <Route
          path="pilots"
          element={
            <EditionRoute>
              <ModuleRoute>
                <PilotsPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        <Route path="pilots/:pilotId" element={<PilotDetailPage />} />
        {/* WP-05 (§ Programs): challenges, scoped to the active company. */}
        <Route
          path="challenges"
          element={
            <EditionRoute>
              <ModuleRoute>
                <ChallengesPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* PLAN-FASE3.md WP-02 (§ Content pill): Communicatie, scoped to
            the active company. */}
        <Route
          path="communications"
          element={
            <EditionRoute>
              <ModuleRoute>
                <CommunicationsPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* PLAN-FASE3.md WP-03 (§ Content pill): Documenten, scoped to the
            active company. */}
        <Route
          path="documents"
          element={
            <EditionRoute>
              <ModuleRoute>
                <DocumentsPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* PLAN-FASE3.md WP-04 (§ Content pill): Rapportages, scoped to the
            active company — the Content pill's third and final subitem. */}
        <Route
          path="reports"
          element={
            <EditionRoute>
              <ModuleRoute>
                <ReportsPage />
              </ModuleRoute>
            </EditionRoute>
          }
        />
        {/* Link identities, erase, platform admins and audit moved under
            `/manage` (§ Beheer, WP-13); see `LEGACY_MANAGE_REDIRECTS` below
            for the old `/admins` and `/audit` paths. */}

        {/* WP-12 (Departments/Users in de normale app): `/departments` and
            `/users` above now render their real, active-company-scoped
            versions (`DepartmentsPage`/`UsersPage`) and `/departments/compare`
            is mounted just above `/departments/:departmentId`. */}

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* `/profile/*` and `/subscription/*` (§ Accountmenu, § Profiel): its
          own full-page shell (`ProfileShell`, `SecondaryShell` + `ProfileNav`)
          — same pattern as `/manage`, not nested under `AppLayout`, so there
          is one left-hand nav here, not the main `Sidebar` plus a second
          local one. Profiel (name, language, region, statistics timezone,
          private fields), Toestemmingen, Voorkeuren (taal/thema) and
          Abonnementen share this one nav. */}
      <Route
        element={
          <RequireAuth>
            <MeProvider>
              <ProfileShell />
            </MeProvider>
          </RequireAuth>
        }
      >
        <Route path="profile" element={<ProfilePage />} />
        <Route path="profile/consents" element={<ConsentsPage />} />
        <Route path="profile/preferences" element={<PreferencesPage />} />

        {/* WP-10 — Abonnement (Mollie, voor de gebruiker zelf); geen rol nodig. */}
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="subscription/checkout" element={<CheckoutPage />} />
        <Route path="subscription/return" element={<SubscriptionReturnPage />} />
        <Route path="subscription/withdraw" element={<WithdrawPage />} />
      </Route>

      {/* `/settings/*` (§ Instellingen, PLAN-FASE2.md WP-08): hangs off the
          account menu's Bedrijf context (`AccountMenu`'s Instellingen link),
          not one of the three main-menu pills — see § Navigatiemodel. Its
          own full-page shell too, same reasoning as `/profile/*` above. No
          extra route guard here: the pages themselves fall back to a "no
          active company" message and disable writes for a non-admin, same
          "API is the boundary" pattern as the rest of this app. */}
      <Route
        element={
          <RequireAuth>
            <MeProvider>
              <SettingsShell />
            </MeProvider>
          </RequireAuth>
        }
      >
        <Route path="settings" element={<SettingsProfilePage />} />
        <Route path="settings/branding" element={<SettingsBrandingPage />} />
        <Route path="settings/privacy" element={<SettingsPrivacyPage />} />
        <Route path="settings/modules" element={<SettingsModulesPage />} />
      </Route>

      {/* `/manage/*` (§ Beheer): platform-admin/company-admin only. See
          `src/manage/navigation.ts` for the menu these mount under, and
          `permissions.ts` for the predicate each page itself checks — the
          menu only decides what is offered to click, not what a 403 does. */}
      <Route
        path="/manage"
        element={
          <RequireAuth>
            <MeProvider>
              <RequireManage>
                <ManageLayout />
              </RequireManage>
            </MeProvider>
          </RequireAuth>
        }
      >
        <Route index element={<ManageIndexPage />} />

        {/* Companies — admin: everything; company admin: name, departments,
            people (see CompanyDetailPage's own permission checks). */}
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/new" element={<CompanyNewPage />} />
        <Route path="companies/:companyId" element={<CompanyDetailPage />} />
        <Route path="companies/:companyId/edit" element={<CompanyEditPage />} />
        <Route path="companies/:companyId/delete" element={<CompanyDeletePage />} />
        <Route path="companies/:companyId/settings" element={<CompanySettingsPage />} />
        <Route path="companies/:companyId/domains" element={<CompanyDomainsPage />} />
        <Route path="companies/:companyId/roles" element={<CompanyRolesPage />} />
        <Route path="companies/:companyId/members/new" element={<CompanyAddPersonPage />} />
        <Route path="companies/:companyId/departments/new" element={<DepartmentNewPage />} />

        {/* Pilots (§ Beheer, WP-07): platform-wide overview, reusing WP-04's
            `GET /pilots` without `company_id` — the API scopes it by role. */}
        <Route path="pilots" element={<PilotsOverviewPage />} />

        {/* People — admin only (§ Beheer › Menu). */}
        <Route path="users" element={<ManageUsersPage />} />
        <Route path="users/link" element={<LinkIdentitiesPage />} />
        <Route path="users/:userId/erase" element={<ErasePage />} />
        <Route path="admins" element={<PlatformAdminsPage />} />

        {/* Audit — admin: everything; company admin: the allowlisted subset. */}
        <Route path="audit" element={<AuditPage />} />

        {/* Subscriptions (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2) — admin
            only (the API also requires `subscriptions.admin`, see
            `src/manage/navigation.ts`). */}
        <Route path="subscriptions/config" element={<SubscriptionsConfigPage />} />
        <Route path="subscriptions/products" element={<SubscriptionsProductsPage />} />
        <Route path="subscriptions/user-ledger" element={<SubscriptionsUserLedgerPage />} />
        {/* Webhook URLs moved onto Config, per provider — old bookmarks still land somewhere useful. */}
        <Route
          path="subscriptions/webhooks"
          element={<Navigate to="/manage/subscriptions/config" replace />}
        />
        {/* Price changes — the endpoint is being retired; old bookmarks land on Products instead. */}
        <Route
          path="subscriptions/price-changes"
          element={<Navigate to="/manage/subscriptions/products" replace />}
        />

        {/* System — admin only; statistics is a placeholder, requests is real. */}
        <Route path="system/statistics" element={<PlaceholderPage section="statistics" />} />
        <Route path="system/requests" element={<RequestsPage />} />

        {/* Support (PLAN-FASE3.md WP-01): platform-staff-only ticket list +
            thread, same shape as System › Requests above — the API 403s
            anyone who is not a platform admin regardless of what the menu
            offers to click. */}
        <Route path="support/tickets" element={<SupportTicketsPage />} />
        <Route path="support/tickets/:ticketId" element={<SupportTicketDetailPage />} />

        {/* Admin overzicht (PLAN-FASE3.md WP-05): platform-staff-only KPI
            summary, same shape as Support/System above. */}
        <Route path="overview" element={<AdminOverviewPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
